import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  VStack,
  HStack,
  Flex,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Textarea,
  Select,
  Heading,
  Divider,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  InputGroup,
  InputLeftElement,
  Checkbox,
  Icon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, DownloadIcon, EditIcon, SearchIcon } from '@chakra-ui/icons';
import { FaCreditCard, FaUndo, FaShippingFast, FaCheckCircle } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import jsPDF from 'jspdf';
import FedExLabelGenerator from '../shared/FedExLabelGenerator';

import { getAuthHeaders } from '../../utils/apiAuth';
const DEFAULT_ADMIN_FEE = 5; // $5.00 per submission when admin_fee is null/0 (matches add_admin_fee_to_submissions.sql)

const defaultShippingForm = {
  shippingMethod: 'fedex',
  shipFee: '',
  trackingNumber: '',
  labelUrl: '',
  serviceType: '',
  shippingCost: '',
  dropoffLocation: '',
  dropoffContact: '',
  dropoffWindow: '',
  deliveryPerson: '',
  deliveryContact: '',
  deliveryWindow: '',
  shippingNotes: ''
};

function effectiveAdminFee(sub) {
  const v = sub.admin_fee;
  if (v != null && v !== '' && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0) return parseFloat(v);
  return DEFAULT_ADMIN_FEE;
}

function BatchManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [batches, setBatches] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [phlebotomists, setPhlebotomists] = useState({});
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isBatchDetailOpen, onOpen: onBatchDetailOpen, onClose: onBatchDetailClose } = useDisclosure();
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');
  const [shippingForm, setShippingForm] = useState(defaultShippingForm);
  const [savingShipping, setSavingShipping] = useState(false);
  const [fedexModalOpen, setFedexModalOpen] = useState(false);
  const [selectedFedexSubmissionId, setSelectedFedexSubmissionId] = useState(null);
  const [chargingInvoice, setChargingInvoice] = useState(false);
  const [markingManualPayment, setMarkingManualPayment] = useState(false);
  const [unmarkingPaidId, setUnmarkingPaidId] = useState(null);
  const [invoicePaymentStatuses, setInvoicePaymentStatuses] = useState({});
  const [chargeConfirmOpen, setChargeConfirmOpen] = useState(false);
  const [batchToCharge, setBatchToCharge] = useState(null);
  const [unmarkPaidConfirmOpen, setUnmarkPaidConfirmOpen] = useState(false);
  const [batchToUnmarkPaid, setBatchToUnmarkPaid] = useState(null);
  const [manualPaymentConfirmOpen, setManualPaymentConfirmOpen] = useState(false);
  const [batchToMarkPaidManually, setBatchToMarkPaidManually] = useState(null);
  // Phase 2 lab timeline: Mark Shipped modal state
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [batchToShip, setBatchToShip] = useState(null);
  const [shipForm, setShipForm] = useState({ carrier: '', tracking_number: '', estimated_arrival: '' });
  const [savingShip, setSavingShip] = useState(false);
  const cancelRef = React.useRef();
  const cancelUnmarkRef = React.useRef();
  const cancelManualPaymentRef = React.useRef();
  const [labs, setLabs] = useState({});
  const [generatingMobileLabBatchId, setGeneratingMobileLabBatchId] = useState(null);
  const [editingBatchNumber, setEditingBatchNumber] = useState(false);
  const [editedBatchNumber, setEditedBatchNumber] = useState('');
  const [savingBatchNumber, setSavingBatchNumber] = useState(false);
  const [labSearch, setLabSearch] = useState('');
  const [tableStatusFilter, setTableStatusFilter] = useState('all');
  const [batchTablePage, setBatchTablePage] = useState(1);
  const [batchTablePerPage, setBatchTablePerPage] = useState(10);
  const [creatingPaymentLinkBatchId, setCreatingPaymentLinkBatchId] = useState(null);
  const [selectedBatchIds, setSelectedBatchIds] = useState(new Set());
  const [creatingBulkPaymentLink, setCreatingBulkPaymentLink] = useState(false);

  const handleEditBatchNumber = () => {
    if (selectedBatch) {
      setEditedBatchNumber(selectedBatch.batch_number || '');
      setEditingBatchNumber(true);
    }
  };

  const handleCancelEditBatchNumber = () => {
    setEditingBatchNumber(false);
    setEditedBatchNumber('');
  };

  const handleSaveBatchNumber = async () => {
    if (!selectedBatch?.id || !editedBatchNumber.trim()) {
      toast({
        title: 'Error',
        description: 'Batch number cannot be empty',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if batch number already exists
    const { data: existingBatch } = await supabase
      .from('submission_batches')
      .select('id')
      .eq('batch_number', editedBatchNumber.trim())
      .neq('id', selectedBatch.id)
      .maybeSingle();

    if (existingBatch) {
      toast({
        title: 'Error',
        description: `Batch number "${editedBatchNumber.trim()}" already exists. Please use a different number.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSavingBatchNumber(true);
    try {
      const { error } = await supabase
        .from('submission_batches')
        .update({ batch_number: editedBatchNumber.trim() })
        .eq('id', selectedBatch.id);

      if (error) throw error;

      // Update local state
      setSelectedBatch(prev => prev ? { ...prev, batch_number: editedBatchNumber.trim() } : null);
      setBatches(prev => prev.map(b => 
        b.id === selectedBatch.id 
          ? { ...b, batch_number: editedBatchNumber.trim() }
          : b
      ));

      toast({
        title: 'Success',
        description: `Batch number updated to ${editedBatchNumber.trim()}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setEditingBatchNumber(false);
      setEditedBatchNumber('');
    } catch (error) {
      console.error('Error updating batch number:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update batch number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingBatchNumber(false);
    }
  };

  useEffect(() => {
    // Parallelize all initial data fetches
    Promise.all([
      fetchBatches(),
      fetchSubmissions(),
      fetchPhlebotomists(),
      fetchLabs()
    ]).catch(error => {
      console.error('Error during initial data fetch:', error);
    });
  }, []);

  // One-time update: Change BATCH-2026-005 to BATCH-2026-004
  useEffect(() => {
    const updateBatchNumber = async () => {
      try {
        // Check if BATCH-2026-004 already exists
        const { data: existingBatch } = await supabase
          .from('submission_batches')
          .select('id')
          .eq('batch_number', 'BATCH-2026-004')
          .maybeSingle();

        if (existingBatch) {
          console.log('BATCH-2026-004 already exists, skipping update');
          return;
        }

        // Find batch with BATCH-2026-005
        const { data: batchToUpdate } = await supabase
          .from('submission_batches')
          .select('id')
          .eq('batch_number', 'BATCH-2026-005')
          .maybeSingle();

        if (batchToUpdate) {
          // Update to BATCH-2026-004
          const { error } = await supabase
            .from('submission_batches')
            .update({ batch_number: 'BATCH-2026-004' })
            .eq('id', batchToUpdate.id);

          if (error) {
            console.error('Error updating batch number:', error);
          } else {
            console.log('Successfully updated BATCH-2026-005 to BATCH-2026-004');
            // Refresh batches to show updated number
            fetchBatches();
          }
        }
      } catch (error) {
        console.error('Error in batch number update:', error);
      }
    };

    // Run the update once after a short delay to ensure batches are loaded
    const timer = setTimeout(updateBatchNumber, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-open batch if batch ID is in URL params (from "Open in Batches" button)
  useEffect(() => {
    const batchIdFromUrl = searchParams.get('batch');
    if (batchIdFromUrl && batches.length > 0) {
      const batch = batches.find(b => b.id === batchIdFromUrl);
      if (batch) {
        viewBatchDetails(batch);
        // Clear the URL param after opening
        searchParams.delete('batch');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [batches, searchParams]);

  // Handle payment success return from Stripe Checkout (single or bulk)
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Single invoice: ?payment=success&invoice=<id>
      // Bulk invoices: ?payment=success&invoices=<id1,id2,...>
      const singleInvoiceId = searchParams.get('invoice');
      const bulkInvoiceIds = searchParams.get('invoices');

      const idsToMark = [];
      if (bulkInvoiceIds) {
        idsToMark.push(...bulkInvoiceIds.split(',').filter(Boolean));
      } else if (singleInvoiceId) {
        idsToMark.push(singleInvoiceId);
      }

      if (idsToMark.length > 0) {
        // Mark all invoices as paid
        (async () => {
          try {
            const { error } = await supabase
              .from('submission_invoices')
              .update({ payment_status: 'paid', updated_at: new Date().toISOString() })
              .in('id', idsToMark);

            if (error) {
              console.error('Failed to mark invoices as paid:', error);
            } else {
              // Refresh invoice statuses
              const { data: updatedInvoices } = await supabase
                .from('submission_invoices')
                .select('id, original_batch_id, payment_status')
                .in('id', idsToMark);

              if (updatedInvoices) {
                setInvoicePaymentStatuses(prev => {
                  const next = { ...prev };
                  updatedInvoices.forEach(inv => {
                    if (inv.original_batch_id) {
                      next[inv.original_batch_id] = inv.payment_status;
                    }
                  });
                  return next;
                });
              }

              toast({
                title: 'Payment successful',
                description: `${idsToMark.length} invoice${idsToMark.length > 1 ? 's' : ''} marked as paid.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
              });
            }
          } catch (err) {
            console.error('Error processing payment success:', err);
          }
        })();
      }

      // Clean URL params
      searchParams.delete('payment');
      searchParams.delete('invoice');
      searchParams.delete('invoices');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  const fetchLabs = async () => {
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('id, name, stripe_customer_id');
      
      if (error) throw error;
      
      // Create a map of lab_id -> lab data (handle UUID as both key formats)
      const labMap = {};
      (data || []).forEach(lab => {
        const idStr = String(lab.id);
        labMap[lab.id] = lab; // Original format
        labMap[idStr] = lab; // String format
        // Also store lowercase for case-insensitive matching
        labMap[idStr.toLowerCase()] = lab;
      });
      setLabs(labMap);
      console.log('Labs loaded in BatchManagement:', Object.keys(labMap).length, 'labs');
    } catch (error) {
      console.error('Error fetching labs:', error);
    }
  };

  const formatInputDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    const tzOffset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - tzOffset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const toIsoString = (value) => (value ? new Date(value).toISOString() : null);

  useEffect(() => {
    try {
      if (selectedBatch && selectedBatch.id) {
        setShippingForm({
          shippingMethod: selectedBatch.shipping_method || 'fedex',
          shipFee: selectedBatch.ship_fee !== null && selectedBatch.ship_fee !== undefined ? selectedBatch.ship_fee.toString() : '',
          trackingNumber: selectedBatch.fedex_tracking_number || '',
          labelUrl: selectedBatch.fedex_label_url || '',
          serviceType: selectedBatch.fedex_service_type || '',
          shippingCost: selectedBatch.fedex_shipping_cost !== null && selectedBatch.fedex_shipping_cost !== undefined
            ? selectedBatch.fedex_shipping_cost.toString()
            : '',
          dropoffLocation: selectedBatch.dropoff_location || '',
          dropoffContact: selectedBatch.dropoff_contact || '',
          dropoffWindow: formatInputDateTime(selectedBatch.dropoff_window),
          deliveryPerson: selectedBatch.delivery_person || '',
          deliveryContact: selectedBatch.delivery_contact || '',
          deliveryWindow: formatInputDateTime(selectedBatch.delivery_window),
          shippingNotes: selectedBatch.shipping_notes || ''
        });

        const firstSubmission = submissions.find(sub => sub.batch_id === selectedBatch.id);
        setSelectedFedexSubmissionId(firstSubmission?.id || null);
      } else {
        setShippingForm(defaultShippingForm);
        setSelectedFedexSubmissionId(null);
      }
    } catch (error) {
      console.error('Error setting shipping form:', error);
      setShippingForm(defaultShippingForm);
    }
  }, [selectedBatch, submissions]);

  const fetchBatches = async () => {
    try {
      // Fetch batches with only columns that exist in the database
      // Note: dropoff/delivery/shipping_notes columns don't exist, code handles missing values with defaults
      const { data, error } = await supabase
        .from('submission_batches')
        .select('id, batch_name, batch_number, description, status, created_at, ship_fee, fedex_tracking_number, fedex_label_url, fedex_service_type, fedex_shipping_cost')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const batchesData = data || [];

      // Fetch invoice data (payment status and invoice numbers) for batches
      if (batchesData.length > 0) {
        const batchIds = batchesData.map(b => b.id);
        const { data: invoices, error: invoiceError } = await supabase
          .from('submission_invoices')
          .select('id, original_batch_id, payment_status, invoice_number, public_token')
          .in('original_batch_id', batchIds);

        if (!invoiceError && invoices) {
          const statusMap = {};
          const invoiceNumberMap = {};
          const invoiceIdMap = {};
          const invoiceTokenMap = {};
          invoices.forEach(inv => {
            if (inv.original_batch_id) {
              statusMap[inv.original_batch_id] = inv.payment_status || 'unpaid';
              invoiceNumberMap[inv.original_batch_id] = inv.invoice_number;
              invoiceIdMap[inv.original_batch_id] = inv.id;
              invoiceTokenMap[inv.original_batch_id] = inv.public_token;
            }
          });
          setInvoicePaymentStatuses(statusMap);
          
          // Add invoice numbers to batches
          const batchesWithInvoiceNumbers = batchesData.map(batch => ({
            ...batch,
            invoice_number: invoiceNumberMap[batch.id] || null,
            invoice_id: invoiceIdMap[batch.id] || null,
            public_token: invoiceTokenMap[batch.id] || null,
          }));
          setBatches(batchesWithInvoiceNumbers);
        } else {
          if (invoiceError) {
            console.warn('Invoice data fetch failed (batches will still show):', invoiceError.message);
          }
          setBatches(batchesData);
        }
      } else {
        setBatches(batchesData);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch batches. Check console for details.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      // Select fields needed for batch management, and join lab data (including stripe_customer_id)
      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select(`
          id,
          batch_id,
          phlebotomist_id,
          admin_fee,
          draw_fee_paid,
          submitted_at,
          patient_name,
          lab_id,
          labs (
            id,
            name,
            stripe_customer_id
          )
        `)
        .order('submitted_at', { ascending: false }));

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleCreatePaymentLink = async (batch) => {
    // Ensure batch has a lab (EPIC or other) via submissions
    const batchLab = getBatchLab(batch);
    if (!batchLab || !batchLab.stripe_customer_id) {
      toast({
        title: 'Missing Stripe customer',
        description: 'This batch does not have an associated lab with a Stripe payment profile.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreatingPaymentLinkBatchId(batch.id);

      // Auto-create invoice if it doesn't exist
      let invoiceId = batch.invoice_id;
      if (!invoiceId) {
        toast({
          title: 'Generating Invoice',
          description: 'Creating invoice first, then generating payment link...',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });

        await generateBatchInvoice(batch);
        await new Promise(resolve => setTimeout(resolve, 1000));

        const { data: newInvoice } = await supabase
          .from('submission_invoices')
          .select('id')
          .eq('original_batch_id', batch.id)
          .maybeSingle();

        if (!newInvoice) {
          throw new Error('Failed to create invoice. Please try generating it manually first.');
        }
        invoiceId = newInvoice.id;
      }

      const response = await fetch('/api/create-invoice-checkout-session', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceId }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error?.message || 'Failed to create payment link');
      }

      if (!result?.url) {
        throw new Error('Payment link URL not returned from server');
      }

      // Open in new tab so you can test, and show copyable link
      try {
        window.open(result.url, '_blank', 'noopener');
      } catch (e) {
        console.warn('Unable to open payment link automatically:', e);
      }

      await navigator.clipboard?.writeText?.(result.url).catch(() => {});

      toast({
        title: 'Payment link created',
        description: 'The link has been opened in a new tab and copied to your clipboard. Send it to the lab to pay with a card.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast({
        title: 'Error creating payment link',
        description: error.message || 'Something went wrong while creating the payment link.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCreatingPaymentLinkBatchId(null);
    }
  };

  const handleToggleBatchSelection = (batchId) => {
    setSelectedBatchIds(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = (unpaidBatches) => {
    const allSelected = unpaidBatches.every(b => selectedBatchIds.has(b.id));
    if (allSelected) {
      setSelectedBatchIds(new Set());
    } else {
      setSelectedBatchIds(new Set(unpaidBatches.map(b => b.id)));
    }
  };

  const handleBulkPaymentLink = async () => {
    if (selectedBatchIds.size === 0) return;

    const selected = batches.filter(b => selectedBatchIds.has(b.id));

    // Validate same lab
    const labSet = new Set();
    selected.forEach(batch => {
      const lab = getBatchLab(batch);
      if (lab) labSet.add(lab.id);
    });

    if (labSet.size === 0) {
      toast({ title: 'No lab found', description: 'Selected batches have no associated lab.', status: 'error', duration: 5000, isClosable: true });
      return;
    }
    if (labSet.size > 1) {
      toast({ title: 'Multiple labs selected', description: 'All selected batches must belong to the same lab. Please select batches from one lab only.', status: 'error', duration: 5000, isClosable: true });
      return;
    }

    const batchLab = getBatchLab(selected[0]);
    if (!batchLab || !batchLab.stripe_customer_id) {
      toast({ title: 'Missing Stripe customer', description: 'The lab does not have a Stripe payment profile.', status: 'error', duration: 5000, isClosable: true });
      return;
    }

    setCreatingBulkPaymentLink(true);
    try {
      // Collect invoice IDs, auto-generating if needed
      const invoiceIds = [];
      for (const batch of selected) {
        let invoiceId = batch.invoice_id;
        if (!invoiceId) {
          // Check if invoice exists for this batch
          const { data: existingInvoice } = await supabase
            .from('submission_invoices')
            .select('id')
            .eq('original_batch_id', batch.id)
            .maybeSingle();

          if (existingInvoice) {
            invoiceId = existingInvoice.id;
          } else {
            // Auto-generate invoice
            toast({ title: 'Generating Invoice', description: `Creating invoice for batch ${batch.batch_number || batch.id}...`, status: 'info', duration: 2000, isClosable: true });
            await generateBatchInvoice(batch, { skipDownload: true });
            await new Promise(resolve => setTimeout(resolve, 1000));

            const { data: newInvoice } = await supabase
              .from('submission_invoices')
              .select('id')
              .eq('original_batch_id', batch.id)
              .maybeSingle();

            if (!newInvoice) {
              throw new Error(`Failed to create invoice for batch ${batch.batch_number || batch.id}`);
            }
            invoiceId = newInvoice.id;
          }
        }
        invoiceIds.push(invoiceId);
      }

      const response = await fetch('/api/create-bulk-checkout-session', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceIds }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error?.message || 'Failed to create bulk payment link');
      }

      if (!result?.url) {
        throw new Error('Payment link URL not returned from server');
      }

      try {
        window.open(result.url, '_blank', 'noopener');
      } catch (e) {
        console.warn('Unable to open payment link automatically:', e);
      }

      await navigator.clipboard?.writeText?.(result.url).catch(() => {});

      toast({
        title: 'Bulk payment link created',
        description: `Payment link for ${selected.length} batches has been opened in a new tab and copied to clipboard.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      setSelectedBatchIds(new Set());
    } catch (error) {
      console.error('Error creating bulk payment link:', error);
      toast({
        title: 'Error creating bulk payment link',
        description: error.message || 'Something went wrong.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCreatingBulkPaymentLink(false);
    }
  };

  const fetchPhlebotomists = async () => {
    try {
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('id, lab_draw_fee, company_name, full_name');

      if (error) throw error;

      const phlebotomistMap = {};
      (data || []).forEach(phleb => {
        phlebotomistMap[phleb.id] = phleb;
      });
      setPhlebotomists(phlebotomistMap);
    } catch (error) {
      console.error('Error fetching phlebotomists:', error);
    }
  };

  const handleCreateBatch = async () => {
    // Batch name is optional - batch number will be auto-generated
    try {
      // Generate batch number with retry on duplicate key
      let data, error, retries = 0;
      while (retries < 3) {
        const batchNumber = await generateBatchNumber();
        const result = await supabase
          .from('submission_batches')
          .insert({
            batch_name: newBatchName || null,
            batch_number: batchNumber,
            description: newBatchDescription || null,
            status: 'pending',
          })
          .select()
          .single();
        data = result.data;
        error = result.error;
        if (!error || !error.message?.includes('duplicate')) break;
        retries++;
      }

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Invoice created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setNewBatchName('');
      setNewBatchDescription('');
      onClose();
      fetchBatches();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddSubmissionsToBatch = async (batchId, submissionIds) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ batch_id: batchId })
        .in('id', submissionIds);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Submissions added to invoice',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchSubmissions();
      fetchBatches();
    } catch (error) {
      console.error('Error adding submissions to invoice:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRemoveSubmissionFromBatch = async (submissionId) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ batch_id: null })
        .eq('id', submissionId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Submission removed from invoice',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchSubmissions();
      fetchBatches();
    } catch (error) {
      console.error('Error removing submission from invoice:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Get lab for a batch (from submissions)
  const getBatchLab = (batch) => {
    const batchSubmissions = submissions.filter(s => s.batch_id === batch.id);
    if (batchSubmissions.length === 0) return null;
    
    // Get first submission (all should be same lab)
    const firstSubmission = batchSubmissions[0];
    if (!firstSubmission) return null;
    
    let lab = null;
    
    // First, try to get lab from joined submission data (most reliable)
    if (firstSubmission.labs) {
      // If labs is an array (Supabase returns as array for one-to-many, but should be object for one-to-one)
      if (Array.isArray(firstSubmission.labs) && firstSubmission.labs.length > 0) {
        lab = firstSubmission.labs[0];
      }
      // If labs is an object (Supabase sometimes returns as object)
      else if (firstSubmission.labs && firstSubmission.labs.name) {
        lab = firstSubmission.labs;
      }
    }
    
    // If we got lab from joined data but it's missing stripe_customer_id, enrich from labs map
    if (lab && !lab.stripe_customer_id && firstSubmission.lab_id) {
      const enrichedLab = labs[firstSubmission.lab_id] || 
                          labs[String(firstSubmission.lab_id)] ||
                          Object.values(labs).find(l => String(l.id) === String(firstSubmission.lab_id));
      if (enrichedLab && enrichedLab.stripe_customer_id) {
        lab = { ...lab, stripe_customer_id: enrichedLab.stripe_customer_id };
      }
    }
    
    // If we didn't get lab from joined data, fallback to labs map lookup
    if (!lab) {
      const labId = firstSubmission.lab_id;
      if (!labId) return null;
      
      // Try direct lookup first
      lab = labs[labId] || labs[String(labId)];
      
      // Fallback: try string comparison for UUID mismatches
      if (!lab) {
        lab = Object.values(labs).find(l => String(l.id) === String(labId));
      }
    }
    
    return lab || null;
  };

  // Check if batch's lab has Stripe customer ID
  const batchLabHasStripe = (batch) => {
    const lab = getBatchLab(batch);
    return lab && lab.stripe_customer_id;
  };

  const calculateBatchFees = (batch) => {
    const batchSubmissions = submissions.filter(s => s.batch_id === batch.id);
    
    let totalDrawFee = 0;
    let totalAdminFee = 0;
    const shipFee = batch.ship_fee ? parseFloat(batch.ship_fee) : 0;

    batchSubmissions.forEach(sub => {
      const phlebotomist = sub.phlebotomist_id ? phlebotomists[sub.phlebotomist_id] : null;
      const drawFee = parseFloat(phlebotomist?.lab_draw_fee || 0);
      const adminFee = effectiveAdminFee(sub);

      totalDrawFee += drawFee;
      totalAdminFee += adminFee;
    });

    const total = totalDrawFee + totalAdminFee + shipFee;

    return {
      totalDrawFee,
      totalAdminFee,
      shipFee,
      total,
      submissionCount: batchSubmissions.length,
    };
  };

  const formatShippingMethod = (method) => {
    switch (method) {
      case 'drop_off':
        return 'Drop-Off';
      case 'delivery':
        return 'Delivery / Courier';
      case 'fedex':
      default:
        return 'FedEx Label';
    }
  };

  // Generate unique batch number in format BATCH-YYYY-NNN (e.g., BATCH-2026-001)
  const generateBatchNumber = async () => {
    const year = new Date().getFullYear();
    const yearStr = year.toString();
    let seqNum = 1;
    let batchNumber;
    let exists = true;
    let attempts = 0;
    const maxAttempts = 999;

    while (exists && attempts < maxAttempts) {
      // Format: BATCH-YYYY-NNN (zero-padded to 3 digits)
      const seqStr = seqNum.toString().padStart(3, '0');
      batchNumber = `BATCH-${yearStr}-${seqStr}`;

      // Check if this batch number already exists
      const { data, error } = await supabase
        .from('submission_batches')
        .select('id')
        .eq('batch_number', batchNumber)
        .maybeSingle();

      if (error) {
        console.error('Error checking batch number:', error);
        break;
      }

      exists = data !== null;
      seqNum++;
      attempts++;
    }

    if (attempts >= maxAttempts) {
      console.error('Failed to generate unique batch number after', maxAttempts, 'attempts');
      // Fallback: use timestamp-based number
      batchNumber = `BATCH-${yearStr}-${Date.now().toString().slice(-3)}`;
    }

    return batchNumber;
  };

  // Generate unique 4-character alphanumeric invoice number
  const generateInvoiceNumber = async () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excludes confusing chars (0, O, I, 1)
    let invoiceNumber;
    let exists = true;
    let attempts = 0;
    const maxAttempts = 100;

    while (exists && attempts < maxAttempts) {
      invoiceNumber = '';
      for (let i = 0; i < 4; i++) {
        const randomIndex = Math.floor(Math.random() * chars.length);
        invoiceNumber += chars[randomIndex];
      }

      // Check if this batch number already exists
      const { data, error } = await supabase
        .from('submission_invoices')
        .select('id')
          .eq('invoice_number', invoiceNumber)
        .maybeSingle();

      if (error) {
        console.error('Error checking invoice number:', error);
        break;
      }

      exists = data !== null;
      attempts++;
    }

    if (attempts >= maxAttempts) {
      console.error('Failed to generate unique invoice number after', maxAttempts, 'attempts');
      // Fallback: use timestamp-based number
      invoiceNumber = Date.now().toString().slice(-4).toUpperCase();
    }

    return invoiceNumber;
  };

  const generateBatchInvoice = async (batch, { skipDownload = false } = {}) => {
    const fees = calculateBatchFees(batch);
    const batchSubmissions = submissions.filter(s => s.batch_id === batch.id);

    try {
      // Step 1: Create or get invoice record in database
      let invoice;
      const invoiceData = {
        invoice_name: batch.batch_name || null, // Make optional
        description: batch.description || null,
        ship_fee: batch.ship_fee || 0,
        shipping_method: batch.shipping_method || 'fedex',
        dropoff_location: batch.dropoff_location || null,
        dropoff_contact: batch.dropoff_contact || null,
        dropoff_window: batch.dropoff_window || null,
        delivery_person: batch.delivery_person || null,
        delivery_contact: batch.delivery_contact || null,
        delivery_window: batch.delivery_window || null,
        shipping_notes: batch.shipping_notes || null,
        fedex_tracking_number: batch.fedex_tracking_number || null,
        fedex_label_url: batch.fedex_label_url || null,
        fedex_shipping_cost: batch.fedex_shipping_cost || null,
        fedex_service_type: batch.fedex_service_type || null,
        shipped_at: batch.shipped_at || null,
        status: batch.status || 'pending',
        invoice_generated_at: new Date().toISOString(),
        notes: null,
        original_batch_id: batch.id || null
      };

      // Check if invoice already exists for this batch
      // First check by original_batch_id if available, otherwise by invoice_name
      let existingInvoice = null;
      if (batch.id) {
        const { data: invoiceByBatchId } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('original_batch_id', batch.id)
          .maybeSingle();
        existingInvoice = invoiceByBatchId;
      }
      
      // If not found by batch_id, try by invoice_name (only if batch_name exists)
      if (!existingInvoice && batch.batch_name) {
        const { data: invoiceByName } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('invoice_name', batch.batch_name)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        existingInvoice = invoiceByName;
      }

      if (existingInvoice) {
        // Update existing invoice
        const { data, error } = await supabase
          .from('submission_invoices')
          .update({
            ...invoiceData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingInvoice.id)
          .select()
          .single();

        if (error) throw error;
        invoice = data;
      } else {
        // Generate invoice number for new invoice
        const invoiceNumber = await generateInvoiceNumber();
        invoiceData.invoice_number = invoiceNumber;

        // Create new invoice
        const { data, error } = await supabase
          .from('submission_invoices')
          .insert(invoiceData)
          .select()
          .single();

        if (error) throw error;
        invoice = data;
      }

      // Step 2: Link submissions to invoice
      const submissionIds = batchSubmissions.map(s => s.id);
      if (submissionIds.length > 0) {
        const { error: updateError } = await supabase
          .from('submissions')
          .update({ invoice_id: invoice.id })
          .in('id', submissionIds);

        if (updateError) throw updateError;
      }

      // Step 3: Fetch lab and phlebotomist names for PDF
      const labIds = [...new Set(batchSubmissions.map(s => s.lab_id).filter(Boolean))];
      const phlebotomistIds = [...new Set(batchSubmissions.map(s => s.phlebotomist_id).filter(Boolean))];
      
      // Fetch lab names
      let labNames = {};
      if (labIds.length > 0) {
        const { data: labData } = await supabase
          .from('labs')
          .select('id, name')
          .in('id', labIds);
        if (labData) {
          labData.forEach(lab => {
            labNames[lab.id] = lab.name;
          });
        }
      }
      
      // Fetch phlebotomist names
      let phlebotomistNames = {};
      if (phlebotomistIds.length > 0) {
        const { data: phlebData } = await supabase
          .from('phlebotomist_profiles')
          .select('id, full_name, company_name')
          .in('id', phlebotomistIds);
        if (phlebData) {
          phlebData.forEach(phleb => {
            phlebotomistNames[phleb.id] = phleb.company_name || phleb.full_name || 'N/A';
          });
        }
      }

      // Step 4: Create PDF with invoice number
      const doc = new jsPDF();
      let yPos = 20;
      const pageWidth = 210; // A4 width in mm
      const margin = 20;

      // Company Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      doc.setFontSize(16);
      doc.text('Invoice', pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      // Invoice Details Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      // Use the most recent blood draw date from submissions instead of today
      const subDates = batchSubmissions.map(s => s.submitted_at ? new Date(s.submitted_at) : null).filter(Boolean);
      const invoiceDate = subDates.length > 0
        ? new Date(Math.max(...subDates.map(d => d.getTime()))).toLocaleDateString()
        : new Date().toLocaleDateString();

      doc.text(`Invoice Number: ${invoice.invoice_number || 'Pending'}`, margin, yPos);
      yPos += 6;
      doc.text(`Date: ${invoiceDate}`, margin, yPos);
      yPos += 10;

      // Company Information Section
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Bill From:', margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.text('At Home Blood Draw Inc', margin, yPos);
      yPos += 6;
      
      // Get unique lab names for this invoice
      const uniqueLabNames = [...new Set(batchSubmissions.map(s => labNames[s.lab_id]).filter(Boolean))];
      if (uniqueLabNames.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Bill To:', margin, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'normal');
        uniqueLabNames.forEach(labName => {
          doc.text(labName, margin, yPos);
          yPos += 6;
        });
      }
      yPos += 4;

      // Invoice Information
      doc.setFontSize(14);
      doc.text('Invoice Information', 20, yPos);
      yPos += 8;
      doc.setFontSize(10);
      if (invoice.invoice_name) {
        doc.text(`Invoice Name: ${invoice.invoice_name}`, margin, yPos);
        yPos += 6;
      }
      doc.text(`Invoice Number: ${invoice.invoice_number || 'Pending'}`, margin, yPos);
      yPos += 6;
      doc.text(`Status: ${invoice.status}`, 20, yPos);
      yPos += 6;
      doc.text(`Submissions: ${fees.submissionCount}`, 20, yPos);
      yPos += 10;

      // Shipping Summary
      doc.setFontSize(12);
      doc.text(`Shipping Method: ${formatShippingMethod(invoice.shipping_method)}`, 20, yPos);
      yPos += 6;
      if (invoice.shipping_method === 'fedex') {
        if (invoice.fedex_tracking_number) {
          doc.text(`FedEx Tracking: ${invoice.fedex_tracking_number}`, 20, yPos);
          yPos += 6;
        }
        if (invoice.fedex_service_type) {
          doc.text(`Service Type: ${invoice.fedex_service_type}`, 20, yPos);
          yPos += 6;
        }
      }
      if (invoice.shipping_method === 'drop_off') {
        if (invoice.dropoff_location) {
          doc.text(`Drop-Off Location: ${invoice.dropoff_location}`, 20, yPos);
          yPos += 6;
        }
        if (invoice.dropoff_window) {
          doc.text(`Drop-Off Window: ${new Date(invoice.dropoff_window).toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
      }
      if (invoice.shipping_method === 'delivery') {
        if (invoice.delivery_person) {
          doc.text(`Courier: ${invoice.delivery_person}`, 20, yPos);
          yPos += 6;
        }
        if (invoice.delivery_window) {
          doc.text(`Delivery Window: ${new Date(invoice.delivery_window).toLocaleString()}`, 20, yPos);
          yPos += 6;
        }
      }
      if (invoice.shipping_notes) {
        doc.text(`Shipping Notes: ${invoice.shipping_notes}`, 20, yPos);
        yPos += 10;
      } else {
        yPos += 4;
      }

      // Line Items
      doc.setFontSize(14);
      doc.text('Line Items', 20, yPos);
      yPos += 8;
      doc.setFontSize(10);

      // Draw Fees
      batchSubmissions.forEach((sub, index) => {
        const phlebotomist = sub.phlebotomist_id ? phlebotomists[sub.phlebotomist_id] : null;
        const rawDrawFee = phlebotomist?.lab_draw_fee || 0;
        const drawFee = parseFloat(rawDrawFee);
        const phlebName = phlebotomistNames[sub.phlebotomist_id] || 'N/A';
        const labName = labNames[sub.lab_id] || 'N/A';

        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.text(`${index + 1}. Draw Fee - ${sub.patient_name || 'N/A'}`, margin, yPos);
        doc.text(`Phlebotomist: ${phlebName}`, margin + 5, yPos + 4);
        doc.text(`Lab: ${labName}`, margin + 5, yPos + 8);
        if (sub.draw_fee_paid) {
          doc.setTextColor(0, 128, 0);
          doc.text(`$${drawFee.toFixed(2)} (Phleb Paid)`, 155, yPos);
          doc.setTextColor(0, 0, 0);
        } else {
          doc.text(`$${drawFee.toFixed(2)}`, 180, yPos);
        }
        yPos += 12;
      });

      // Admin Fees (use default $5 when admin_fee is null/0 so invoice always includes admin fee)
      batchSubmissions.forEach((sub, index) => {
        const adminFee = effectiveAdminFee(sub);
        const phlebName = phlebotomistNames[sub.phlebotomist_id] || 'N/A';
        const labName = labNames[sub.lab_id] || 'N/A';
        
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.text(`Admin Fee - ${sub.patient_name || 'N/A'}`, margin, yPos);
        doc.text(`Phlebotomist: ${phlebName}`, margin + 5, yPos + 4);
        doc.text(`Lab: ${labName}`, margin + 5, yPos + 8);
        doc.text(`$${adminFee.toFixed(2)}`, 180, yPos);
        yPos += 12;
      });

      // Ship Fee (once per invoice)
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.text('Shipping Fee', margin, yPos);
      doc.text(`$${fees.shipFee.toFixed(2)}`, 180, yPos);
      yPos += 10;

      // Total
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', margin, yPos);
      doc.text(`$${fees.total.toFixed(2)}`, 180, yPos);
      yPos += 15;

      // Footer with company info
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      doc.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });

      // Generate PDF blob and create download link
      if (!skipDownload) {
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Invoice-${invoice.invoice_number || invoice.id}.pdf`;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }

      // Step 4: Update invoice with PDF URL (if you want to store it)
      // Note: You may want to upload the PDF to storage and store the URL
      // For now, we'll just mark it as generated
      await supabase
        .from('submission_invoices')
        .update({
          invoice_generated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', invoice.id);

      // Also update batch for backward compatibility
      await supabase
        .from('submission_batches')
        .update({
          invoice_generated_at: new Date().toISOString(),
        })
        .eq('id', batch.id);

      toast({
        title: 'Invoice Created',
        description: `Invoice ${invoice.invoice_number} created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchBatches();
      fetchSubmissions();

      // Refresh invoice data if modal is open
      if (selectedBatch && isBatchDetailOpen) {
        const { data: updatedInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('id', invoice.id)
          .single();
        
        if (updatedInvoice) {
          setSelectedInvoice(updatedInvoice);
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate invoice',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const generateMobileLabInvoice = async (batch) => {
    const batchSubmissions = submissions.filter(s => s.batch_id === batch.id);
    if (!batchSubmissions.length) {
      toast({ title: 'No submissions', description: 'This batch has no submissions.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    const phlebotomistIds = [...new Set(batchSubmissions.map(s => s.phlebotomist_id).filter(Boolean))];
    if (!phlebotomistIds.length) {
      toast({ title: 'No phlebotomists', description: 'No phlebotomist assigned to submissions in this batch.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setGeneratingMobileLabBatchId(batch.id);
    try {
      const { data: phlebData } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, company_name')
        .in('id', phlebotomistIds);
      const phlebotomistNames = {};
      (phlebData || []).forEach(p => {
        phlebotomistNames[p.id] = p.company_name || p.full_name || 'N/A';
      });

      const doc = new jsPDF();
      const pageWidth = 210;
      const margin = 20;
      const batchRef = batch.batch_number || batch.id;
      const batchLabel = typeof batchRef === 'string' && batchRef.length < 20 ? batchRef : `Batch ${String(batch.id).slice(0, 8)}`;
      const fileRef = batch.batch_number || String(batch.id).slice(0, 8);

      phlebotomistIds.forEach((phlebId, idx) => {
        const subs = batchSubmissions.filter(s => s.phlebotomist_id === phlebId);
        const phlebName = phlebotomistNames[phlebId] || 'N/A';
        let total = 0;
        const lineItems = [];
        subs.forEach(sub => {
          const phleb = phlebotomists[sub.phlebotomist_id];
          const fee = phleb?.lab_draw_fee != null && phleb?.lab_draw_fee !== '' ? parseFloat(phleb.lab_draw_fee) : 0;
          lineItems.push({ patient: sub.patient_name || 'N/A', fee });
          total += fee;
        });

        if (idx > 0) doc.addPage();
        let yPos = 20;

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
        yPos += 8;
        doc.setFontSize(16);
        doc.text('Phlebotomist Invoice', pageWidth / 2, yPos, { align: 'center' });
        yPos += 12;

        // Use the most recent blood draw date from submissions instead of today
        const subDates = subs.map(s => s.submitted_at ? new Date(s.submitted_at) : null).filter(Boolean);
        const invoiceDate = subDates.length > 0
          ? new Date(Math.max(...subDates.map(d => d.getTime()))).toLocaleDateString()
          : new Date().toLocaleDateString();

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`Date: ${invoiceDate}`, margin, yPos);
        yPos += 6;
        doc.text(`Batch: ${batchLabel}`, margin, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'bold');
        doc.text('Bill To:', margin, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'normal');
        doc.text(phlebName, margin, yPos);
        yPos += 10;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Line Items (agreed draw fee only; no admin or shipping)', margin, yPos);
        yPos += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        lineItems.forEach(({ patient, fee }) => {
          if (yPos > 250) { doc.addPage(); yPos = 20; }
          doc.text(`Draw Fee - ${patient}`, margin, yPos);
          doc.text(`$${parseFloat(fee).toFixed(2)}`, 180, yPos);
          yPos += 8;
        });

        if (yPos > 250) { doc.addPage(); yPos = 20; }
        yPos += 4;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Total:', margin, yPos);
        doc.text(`$${total.toFixed(2)}`, 180, yPos);
        yPos += 12;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
        yPos += 4;
        doc.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });
      });

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Phlebotomist-Invoices-${fileRef}.pdf`;
      link.click();
      URL.revokeObjectURL(pdfUrl);

      toast({
        title: 'Phlebotomist Invoice(s) Created',
        description: phlebotomistIds.length === 1
          ? `Invoice for ${phlebotomistNames[phlebotomistIds[0]]} downloaded.`
          : `${phlebotomistIds.length} phlebotomist invoice(s) downloaded.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error generating phlebotomist invoice:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate phlebotomist invoice(s)',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGeneratingMobileLabBatchId(null);
    }
  };

  const viewBatchDetails = async (batch) => {
    try {
      if (!batch || !batch.id) {
        toast({
          title: 'Error',
          description: 'Invalid batch data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setSelectedBatch(batch);
      
      // Try to fetch the associated invoice if it exists
      try {
        const { data: invoice, error } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('original_batch_id', batch.id)
          .maybeSingle();
        
        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error fetching invoice:', error);
        }
        
        setSelectedInvoice(invoice || null);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setSelectedInvoice(null);
      }
      
      onBatchDetailOpen();
    } catch (error) {
      console.error('Error opening batch details:', error);
      toast({
        title: 'Error',
        description: 'Failed to open invoice details. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShippingFieldChange = (field, value) => {
    setShippingForm((prev) => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-set ship fee to $10 when drop_off is selected
      if (field === 'shippingMethod' && value === 'drop_off') {
        updated.shipFee = '10.00';
      }
      
      return updated;
    });
  };

  const handleSaveShipping = async () => {
    if (!selectedBatch) return;
    setSavingShipping(true);

    // Calculate ship_fee based on shipping method
    let shipFee = null;
    if (shippingForm.shippingMethod === 'drop_off') {
      // Drop off: fixed $10
      shipFee = 10.00;
    } else if (shippingForm.shippingMethod === 'fedex') {
      // FedEx: shipping cost + $10 (if shipping cost exists)
      const fedexCost = shippingForm.shippingCost !== '' ? parseFloat(shippingForm.shippingCost) : null;
      if (fedexCost !== null) {
        shipFee = fedexCost + 10.00;
      } else if (shippingForm.shipFee !== '') {
        // Use manually entered fee if no FedEx cost yet
        shipFee = parseFloat(shippingForm.shipFee);
      }
    } else {
      // Other methods: use entered fee
      shipFee = shippingForm.shipFee !== '' ? parseFloat(shippingForm.shipFee) : null;
    }

    // Only include columns that actually exist in the database
    const payload = {
      ship_fee: shipFee,
      fedex_tracking_number: shippingForm.trackingNumber || null,
      fedex_label_url: shippingForm.labelUrl || null,
      fedex_service_type: shippingForm.serviceType || null,
      fedex_shipping_cost: shippingForm.shippingCost !== '' ? parseFloat(shippingForm.shippingCost) : null,
      // Note: shipping_method, dropoff_*, delivery_*, shipping_notes columns don't exist in DB
      // These fields are stored in form state only for UI purposes
    };

    try {
      const { error } = await supabase
        .from('submission_batches')
        .update(payload)
        .eq('id', selectedBatch.id);

      if (error) throw error;

      toast({
        title: 'Shipping updated',
        description: 'Invoice shipping details saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      setSelectedBatch((prev) => (prev ? { ...prev, ...payload } : prev));
      fetchBatches();
    } catch (error) {
      console.error('Error saving shipping details:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setSavingShipping(false);
    }
  };

  const openFedexModal = () => {
    if (!selectedFedexSubmissionId) {
      toast({
        title: 'Select a submission',
        description: 'Choose which submission to use for the FedEx label',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    setFedexModalOpen(true);
  };

  const closeFedexModal = () => {
    setFedexModalOpen(false);
  };

  const handleChargeInvoice = () => {
    if (selectedBatch) {
      handleChargeClick(selectedBatch);
    }
  };

  const handleChargeClick = (batch) => {
    setBatchToCharge(batch);
    setChargeConfirmOpen(true);
  };

  const confirmChargeInvoice = async () => {
    setChargeConfirmOpen(false);
    if (batchToCharge) {
      await chargeInvoiceFromBatch(batchToCharge);
      setBatchToCharge(null);
    }
  };

  const chargeInvoiceFromBatch = async (batch) => {
    setChargingInvoice(true);
    try {
      // Check if lab has Stripe customer ID
      const lab = getBatchLab(batch);
      if (!lab) {
        toast({
          title: 'Error',
          description: 'Cannot determine lab for this batch. Batch must have submissions with a lab assigned.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!lab.stripe_customer_id) {
        toast({
          title: 'Stripe Not Set Up',
          description: `Lab "${lab.name}" does not have a Stripe customer ID. Please set up payment method in Lab Management first.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // First, try to fetch existing invoice
      let invoice = null;
      const { data: existingInvoice } = await supabase
        .from('submission_invoices')
        .select('*')
        .eq('original_batch_id', batch.id)
        .maybeSingle();
      
      invoice = existingInvoice;

      // If no invoice exists, generate it first
      if (!invoice) {
        toast({
          title: 'Generating Invoice',
          description: 'Creating invoice first, then charging...',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
        
        // Generate invoice first
        await generateBatchInvoice(batch);
        
        // Wait a moment for invoice to be created
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Fetch the newly created invoice
        const { data: newInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('original_batch_id', batch.id)
          .maybeSingle();
        
        if (!newInvoice) {
          toast({
            title: 'Error',
            description: 'Failed to create invoice. Please try generating invoice manually first.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        
        invoice = newInvoice;
      }

      if (invoice.payment_status === 'paid') {
        toast({
          title: 'Already Paid',
          description: 'This invoice has already been paid.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const response = await fetch('/api/charge-invoice', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          invoiceId: invoice.id
        }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      if (!isJson) {
        const text = await response.text();
        if (text.trim().startsWith('<')) {
          throw new Error(
            'Payment API is not available (charge-invoice endpoint not configured). Use the Invoices page to mark this invoice as Paid manually.'
          );
        }
        throw new Error(text || `Server returned ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        const msg = typeof result.error === 'object'
          ? (result.error.message || result.error.details || JSON.stringify(result.error))
          : result.error;
        throw new Error(msg);
      }

      // lab is already declared above, reuse it
      toast({
        title: 'Payment Successful',
        description: result.message || `Invoice charged successfully. ${lab ? `Charged ${lab.name}` : ''} - Amount: $${result.invoice.amount_paid.toFixed(2)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Update payment status in state
      setInvoicePaymentStatuses(prev => ({
        ...prev,
        [batch.id]: 'paid'
      }));
      
      fetchBatches();
      
      // Refresh invoice if modal is open
      if (selectedBatch && selectedBatch.id === batch.id) {
        const { data: updatedInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('id', invoice.id)
          .single();

        if (updatedInvoice) {
          setSelectedInvoice(updatedInvoice);
        }
      }
    } catch (error) {
      console.error('Error charging invoice:', error);
      toast({
        title: 'Payment Failed',
        description: error.message || 'Failed to charge invoice',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChargingInvoice(false);
    }
  };

  const confirmManualPayment = () => {
    if (batchToMarkPaidManually) {
      markBatchInvoicePaidManually(batchToMarkPaidManually);
    }
    setManualPaymentConfirmOpen(false);
    setBatchToMarkPaidManually(null);
  };

  const markBatchInvoicePaidManually = async (batch) => {
    if (!batch) return;
    setMarkingManualPayment(true);
    try {
      let invoice = null;
      const { data: existingInvoice } = await supabase
        .from('submission_invoices')
        .select('*')
        .eq('original_batch_id', batch.id)
        .maybeSingle();
      invoice = existingInvoice;

      if (!invoice) {
        await generateBatchInvoice(batch);
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: newInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('original_batch_id', batch.id)
          .maybeSingle();
        if (!newInvoice) {
          toast({
            title: 'Error',
            description: 'Could not create or find invoice. Try generating the invoice first.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        invoice = newInvoice;
      }

      if (invoice.payment_status === 'paid') {
        toast({
          title: 'Already Paid',
          description: 'This invoice is already marked as paid.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        setChargeConfirmOpen(false);
        setBatchToCharge(null);
        return;
      }

      const { error } = await supabase
        .from('submission_invoices')
        .update({ payment_status: 'paid', updated_at: new Date().toISOString() })
        .eq('id', invoice.id);
      if (error) throw error;

      const fees = calculateBatchFees(batch);
      const totalAmount = fees.total;
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const markedBy = adminUser?.name ? ` Marked by: ${adminUser.name}` : '';
      const lab = getBatchLab(batch);

      // Always log to admin_activity_log so it shows on Dashboard Recent Activity (even when no lab or $0)
      try {
        await supabase.from('admin_activity_log').insert({
          action: 'Marked batch as paid',
          username: adminUser?.name || 'Admin',
          details: `${batch.batch_number || batch.batch_name || 'Batch'} — $${totalAmount.toFixed(2)}${lab ? '' : ' (no lab assigned)'}`,
          created_at: new Date().toISOString(),
        });
      } catch (logErr) {
        console.error('Error logging admin activity:', logErr);
      }

      // Create billing_transactions record when we have a lab and amount > 0 (required for Payment History and dashboard payments)
      if (lab && lab.id && totalAmount > 0) {
        try {
          await supabase
            .from('billing_transactions')
            .insert({
              lab_id: lab.id,
              amount: totalAmount,
              currency: 'USD',
              status: 'completed',
              type: 'one_time',
              description: `Manual payment - Invoice ${invoice.invoice_number || invoice.id} - ${batch.batch_name || batch.batch_number || 'Batch'}.${markedBy}`,
              stripe_payment_intent_id: null,
              stripe_transaction_id: `manual_${invoice.id}`,
              payment_method_type: 'manual',
              transaction_date: new Date().toISOString(),
              metadata: {
                invoice_id: invoice.id,
                invoice_number: invoice.invoice_number,
                batch_id: batch.id,
                payment_type: 'manual',
                marked_by: adminUser?.name || null,
                lab_name: lab.name || null
              }
            });
        } catch (txError) {
          console.error('Error creating billing_transactions record:', txError);
        }
      }

      toast({
        title: 'Manual payment recorded',
        description: `Invoice ${invoice.invoice_number || invoice.id} marked as paid.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      setInvoicePaymentStatuses(prev => ({ ...prev, [batch.id]: 'paid' }));
      fetchBatches();
      setChargeConfirmOpen(false);
      setBatchToCharge(null);
      setManualPaymentConfirmOpen(false);
      setBatchToMarkPaidManually(null);

      if (selectedBatch && selectedBatch.id === batch.id) {
        const { data: updatedInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('id', invoice.id)
          .single();
        if (updatedInvoice) setSelectedInvoice(updatedInvoice);
      }
    } catch (error) {
      console.error('Error marking invoice paid manually:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to mark invoice as paid',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMarkingManualPayment(false);
    }
  };

  const handleUnmarkPaidClick = (batch) => {
    setBatchToUnmarkPaid(batch);
    setUnmarkPaidConfirmOpen(true);
  };

  // Phase 2 lab timeline: open the Mark Shipped modal (or undo if already shipped)
  const handleMarkShippedClick = (batch) => {
    setBatchToShip(batch);
    setShipForm({
      carrier: batch.carrier || '',
      tracking_number: batch.tracking_number || '',
      estimated_arrival: batch.estimated_arrival || '',
    });
    setShipModalOpen(true);
  };

  const confirmMarkShipped = async () => {
    if (!batchToShip) return;
    setSavingShip(true);
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const payload = {
        shipped_at: new Date().toISOString(),
        carrier: shipForm.carrier?.trim() || null,
        tracking_number: shipForm.tracking_number?.trim() || null,
        estimated_arrival: shipForm.estimated_arrival || null,
        shipped_by: adminUser?.id || null,
      };
      const { error } = await supabase
        .from('submission_batches')
        .update(payload)
        .eq('id', batchToShip.id);
      if (error) throw error;
      // Refresh local list — caller's fetch effect picks this up too
      setBatches(prev => prev.map(b => b.id === batchToShip.id ? { ...b, ...payload } : b));
      toast({ title: 'Batch marked as shipped', status: 'success', duration: 2500, isClosable: true });
      setShipModalOpen(false);
      setBatchToShip(null);
    } catch (err) {
      toast({ title: 'Failed to mark shipped', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSavingShip(false);
    }
  };

  const undoShipped = async (batch) => {
    if (!batch) return;
    try {
      const { error } = await supabase
        .from('submission_batches')
        .update({ shipped_at: null, shipped_by: null })
        .eq('id', batch.id);
      if (error) throw error;
      setBatches(prev => prev.map(b => b.id === batch.id ? { ...b, shipped_at: null, shipped_by: null } : b));
      toast({ title: 'Shipped status reverted', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to undo', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const confirmUnmarkPaid = async () => {
    if (!batchToUnmarkPaid) return;
    setUnmarkingPaidId(batchToUnmarkPaid.id);
    try {
      const { data: invoice } = await supabase
        .from('submission_invoices')
        .select('*')
        .eq('original_batch_id', batchToUnmarkPaid.id)
        .maybeSingle();

      if (!invoice) {
        toast({
          title: 'Error',
          description: 'Invoice not found for this batch.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const { error } = await supabase
        .from('submission_invoices')
        .update({ payment_status: 'unpaid', updated_at: new Date().toISOString() })
        .eq('id', invoice.id);
      if (error) throw error;

      toast({
        title: 'Payment status reverted',
        description: `Invoice ${invoice.invoice_number || invoice.id} marked as unpaid.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setInvoicePaymentStatuses(prev => ({ ...prev, [batchToUnmarkPaid.id]: 'unpaid' }));
      fetchBatches();
      setUnmarkPaidConfirmOpen(false);
      setBatchToUnmarkPaid(null);

      if (selectedBatch && selectedBatch.id === batchToUnmarkPaid.id) {
        const { data: updatedInvoice } = await supabase
          .from('submission_invoices')
          .select('*')
          .eq('id', invoice.id)
          .single();
        if (updatedInvoice) setSelectedInvoice(updatedInvoice);
      }
    } catch (error) {
      console.error('Error unmarking invoice paid:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to revert payment status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUnmarkingPaidId(null);
    }
  };

  const handleBatchFedexLabelGenerated = async (labelData) => {
    const shippingCost = labelData?.totalNetCharge?.amount ?? null;
    // FedEx fee = FedEx shipping cost + $10 service fee
    const fedexFee = shippingCost !== null ? shippingCost + 10 : null;
    
    // Only include columns that actually exist in the database
    const updates = {
      fedex_tracking_number: labelData?.trackingNumber || null,
      fedex_label_url: labelData?.labelUrl || null,
      fedex_service_type: labelData?.serviceType || null,
      fedex_shipping_cost: shippingCost,
      ship_fee: fedexFee, // Total fee = FedEx cost + $10
      status: 'ready_to_ship'
      // Note: shipping_method column doesn't exist in DB
    };

    setShippingForm((prev) => ({
      ...prev,
      shippingMethod: 'fedex',
      trackingNumber: updates.fedex_tracking_number || prev.trackingNumber,
      labelUrl: updates.fedex_label_url || prev.labelUrl,
      serviceType: updates.fedex_service_type || prev.serviceType,
      shippingCost: shippingCost !== null ? shippingCost.toString() : prev.shippingCost,
      shipFee: fedexFee !== null ? fedexFee.toFixed(2) : prev.shipFee
    }));

    try {
      const { error } = await supabase
        .from('submission_batches')
        .update(updates)
        .eq('id', selectedBatch.id);

      if (error) throw error;

      toast({
        title: 'FedEx label saved to invoice',
        description: `Tracking number ${labelData?.trackingNumber || ''}`,
        status: 'success',
        duration: 4000,
        isClosable: true
      });

      setSelectedBatch((prev) => (prev ? { ...prev, ...updates } : prev));
      fetchBatches();
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating batch with FedEx data:', error);
      toast({
        title: 'Error updating invoice',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      closeFedexModal();
    }
  };

  const filteredBatches = useMemo(() => {
    const labQuery = (labSearch || '').trim().toLowerCase();
    const statusVal = tableStatusFilter;
    return batches.filter((batch) => {
      const batchLab = getBatchLab(batch);
      const labMatch = !labQuery || (batchLab && batchLab.name.toLowerCase().includes(labQuery));
      const displayStatus = invoicePaymentStatuses[batch.id] === 'paid' ? 'paid' : (batch.status || 'pending');
      const statusMatch = statusVal === 'all' || displayStatus === statusVal;
      return labMatch && statusMatch;
    });
  }, [batches, labSearch, tableStatusFilter, invoicePaymentStatuses, submissions]);

  useEffect(() => {
    setBatchTablePage(1);
  }, [labSearch, tableStatusFilter]);

  const batchTableTotalPages = Math.max(1, Math.ceil(filteredBatches.length / batchTablePerPage));

  useEffect(() => {
    setBatchTablePage((p) => Math.min(p, batchTableTotalPages));
  }, [batchTableTotalPages]);

  const batchTableStartIndex = (batchTablePage - 1) * batchTablePerPage;
  const paginatedBatches = useMemo(() => {
    return filteredBatches.slice(batchTableStartIndex, batchTableStartIndex + batchTablePerPage);
  }, [filteredBatches, batchTableStartIndex, batchTablePerPage]);

  // Determine the lab of currently selected batches (null if none selected)
  const selectedLabId = useMemo(() => {
    if (selectedBatchIds.size === 0) return null;
    for (const bId of selectedBatchIds) {
      const batch = batches.find(b => b.id === bId);
      if (batch) {
        const lab = getBatchLab(batch);
        if (lab) return lab.id;
      }
    }
    return null;
  }, [selectedBatchIds, batches, submissions, labs]);

  if (loading) {
    return <Text>Loading invoices...</Text>;
  }

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.800">Submission Invoices</Heading>
        <Button 
          leftIcon={<AddIcon />} 
          colorScheme="blue" 
          onClick={onOpen}
          size="md"
          fontWeight="semibold"
        >
          Create New Invoice
        </Button>
      </Flex>

      <Flex direction={{ base: 'column', md: 'row' }} gap={4} mb={4} flexWrap="wrap" align="flex-end">
        <InputGroup maxW="240px" size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by lab..."
            value={labSearch}
            onChange={(e) => setLabSearch(e.target.value)}
            bg="white"
          />
        </InputGroup>
        <Select
          maxW="180px"
          size="sm"
          value={tableStatusFilter}
          onChange={(e) => setTableStatusFilter(e.target.value)}
          bg="white"
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </Select>
        {(labSearch || tableStatusFilter !== 'all') && (
          <Button size="sm" variant="ghost" onClick={() => { setLabSearch(''); setTableStatusFilter('all'); }}>
            Clear filters
          </Button>
        )}
      </Flex>

      {selectedBatchIds.size > 0 && (() => {
        const selected = batches.filter(b => selectedBatchIds.has(b.id));
        const combinedTotal = selected.reduce((sum, b) => sum + calculateBatchFees(b).total, 0);
        return (
          <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg" p={3} mb={3}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
              <HStack spacing={3}>
                <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                  {selectedBatchIds.size} batch{selectedBatchIds.size > 1 ? 'es' : ''} selected
                </Badge>
                <Text fontWeight="bold" color="green.600" fontSize="sm">
                  Combined Total: ${combinedTotal.toFixed(2)}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<FaCreditCard />}
                  onClick={handleBulkPaymentLink}
                  isLoading={creatingBulkPaymentLink}
                  loadingText="Creating..."
                  fontWeight="semibold"
                >
                  Create Payment Link ({selectedBatchIds.size})
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedBatchIds(new Set())}
                >
                  Clear Selection
                </Button>
              </HStack>
            </Flex>
          </Box>
        );
      })()}

      <Card boxShadow="sm" borderRadius="lg" overflow="hidden">
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th w="40px" py={4} px={2}>
                  {(() => {
                    const selectable = paginatedBatches.filter(b => {
                      if (invoicePaymentStatuses[b.id] === 'paid') return false;
                      if (selectedLabId) {
                        const lab = getBatchLab(b);
                        if (!lab || lab.id !== selectedLabId) return false;
                      }
                      return true;
                    });
                    return (
                      <Checkbox
                        isChecked={selectable.length > 0 && selectable.every(b => selectedBatchIds.has(b.id))}
                        isIndeterminate={selectable.some(b => selectedBatchIds.has(b.id)) && !selectable.every(b => selectedBatchIds.has(b.id))}
                        onChange={() => handleToggleSelectAll(selectable)}
                        colorScheme="blue"
                        size="lg"
                        isDisabled={selectable.length === 0}
                        sx={{ '.chakra-checkbox__control': { borderWidth: '2px', borderColor: 'gray.400' } }}
                      />
                    );
                  })()}
                </Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Batch Number</Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Batch Name</Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Lab</Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Status</Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Phleb</Th>
                <Th fontWeight="semibold" color="gray.700" py={4}>Phleb Status</Th>
                <Th fontWeight="semibold" color="gray.700" py={4} isNumeric>Submissions</Th>
                <Th fontWeight="semibold" color="gray.700" py={4} isNumeric>Total Amount</Th>
                <Th fontWeight="semibold" color="gray.700" py={4} whiteSpace="nowrap" minW="500px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedBatches.length === 0 ? (
                <Tr>
                  <Td colSpan={10} py={10}>
                    <Text textAlign="center" color="gray.500">
                      No submission invoices match your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : paginatedBatches.map((batch, index) => {
                const fees = calculateBatchFees(batch);
                const batchLab = getBatchLab(batch);
                const isPaid = invoicePaymentStatuses[batch.id] === 'paid';
                const batchSubs = submissions.filter(s => s.batch_id === batch.id);
                const drawFeesPaidCount = batchSubs.filter(s => s.draw_fee_paid).length;
                const drawFeesTotal = batchSubs.length;
                const firstSub = batchSubs[0];
                const batchPhleb = firstSub?.phlebotomist_id ? phlebotomists[firstSub.phlebotomist_id] : null;
                const phlebName = batchPhleb?.company_name || batchPhleb?.full_name || firstSub?.phlebotomist_name || null;
                return (
                  <Tr
                    key={batch.id}
                    _hover={{ bg: 'gray.50' }}
                    bg={selectedBatchIds.has(batch.id) ? 'blue.50' : (index % 2 === 0 ? 'white' : 'gray.25')}
                    transition="background 0.2s"
                  >
                    <Td py={4} px={2} w="40px">
                      {!isPaid ? (() => {
                        const differentLab = selectedLabId && batchLab && batchLab.id !== selectedLabId;
                        return (
                          <Tooltip
                            label={differentLab ? 'Different lab — clear selection first' : ''}
                            isDisabled={!differentLab}
                            hasArrow
                          >
                            <Box>
                              <Checkbox
                                isChecked={selectedBatchIds.has(batch.id)}
                                onChange={() => handleToggleBatchSelection(batch.id)}
                                colorScheme="blue"
                                size="lg"
                                isDisabled={differentLab}
                                sx={{ '.chakra-checkbox__control': { borderWidth: '2px', borderColor: 'gray.400' } }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })() : null}
                    </Td>
                    <Td py={4}>
                      <Text fontWeight="bold" color="gray.800" fontSize="sm">
                        {batch.batch_number || 'Pending'}
                      </Text>
                    </Td>
                    <Td py={4}>
                      <Text fontSize="sm" color="gray.700">
                        {batch.batch_name || 'N/A'}
                      </Text>
                    </Td>
                    <Td py={4}>
                      <Badge 
                        colorScheme={batchLab ? "blue" : "red"} 
                        variant={batchLab ? "subtle" : "solid"}
                        fontSize="xs"
                      >
                        {batchLab ? batchLab.name : 'Not assigned'}
                      </Badge>
                    </Td>
                    <Td py={4}>
                      {invoicePaymentStatuses[batch.id] === 'paid' ? (
                        <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                          Paid
                        </Badge>
                      ) : (
                        <Badge 
                          colorScheme={batch.status === 'shipped' ? 'green' : 'yellow'} 
                          fontSize="xs"
                          px={2}
                          py={1}
                        >
                          {batch.status || 'Pending'}
                        </Badge>
                      )}
                    </Td>
                    <Td py={4}>
                      <Badge
                        colorScheme={phlebName ? "purple" : "red"}
                        variant={phlebName ? "subtle" : "solid"}
                        fontSize="xs"
                      >
                        {phlebName || 'Not assigned'}
                      </Badge>
                    </Td>
                    <Td py={4}>
                      {drawFeesPaidCount === drawFeesTotal && drawFeesTotal > 0 ? (
                        <Badge colorScheme="green" fontSize="xs" px={2} py={1} cursor="pointer" _hover={{ opacity: 0.8 }}
                          onClick={async () => {
                            const subIds = batchSubs.map(s => s.id);
                            const { error } = await supabase.from('submissions').update({ draw_fee_paid: false }).in('id', subIds);
                            if (error) { toast({ title: 'Error', description: error.message, status: 'error', duration: 3000 }); return; }
                            setSubmissions(prev => prev.map(s => subIds.includes(s.id) ? { ...s, draw_fee_paid: false } : s));
                            toast({ title: 'Marked as unpaid', status: 'info', duration: 2000 });
                          }}
                        >PAID</Badge>
                      ) : drawFeesPaidCount > 0 ? (
                        <Badge colorScheme="orange" fontSize="xs" px={2} py={1} cursor="pointer" _hover={{ opacity: 0.8 }}
                          onClick={async () => {
                            const subIds = batchSubs.map(s => s.id);
                            const { error } = await supabase.from('submissions').update({ draw_fee_paid: true }).in('id', subIds);
                            if (error) { toast({ title: 'Error', description: error.message, status: 'error', duration: 3000 }); return; }
                            setSubmissions(prev => prev.map(s => subIds.includes(s.id) ? { ...s, draw_fee_paid: true } : s));
                            toast({ title: 'All marked as paid', status: 'success', duration: 2000 });
                          }}
                        >{drawFeesPaidCount}/{drawFeesTotal} PAID</Badge>
                      ) : (
                        <Badge colorScheme="red" variant="subtle" fontSize="xs" px={2} py={1} cursor="pointer" _hover={{ opacity: 0.8 }}
                          onClick={async () => {
                            const subIds = batchSubs.map(s => s.id);
                            const { error } = await supabase.from('submissions').update({ draw_fee_paid: true }).in('id', subIds);
                            if (error) { toast({ title: 'Error', description: error.message, status: 'error', duration: 3000 }); return; }
                            setSubmissions(prev => prev.map(s => subIds.includes(s.id) ? { ...s, draw_fee_paid: true } : s));
                            toast({ title: 'All marked as paid', status: 'success', duration: 2000 });
                          }}
                        >UNPAID</Badge>
                      )}
                    </Td>
                    <Td py={4} isNumeric>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {fees.submissionCount}
                      </Text>
                    </Td>
                    <Td py={4} isNumeric>
                      <Text fontWeight="bold" color="green.600" fontSize="sm">
                        ${fees.total.toFixed(2)}
                      </Text>
                    </Td>
                    <Td py={4}>
                      <Flex flexWrap="wrap" gap={2} align="center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => viewBatchDetails(batch)}
                          fontSize="xs"
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          leftIcon={<DownloadIcon />}
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => generateBatchInvoice(batch)}
                          fontSize="xs"
                        >
                          Create Invoice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<DownloadIcon />}
                          onClick={() => generateMobileLabInvoice(batch)}
                          isLoading={generatingMobileLabBatchId === batch.id}
                          isDisabled={!!generatingMobileLabBatchId}
                          fontSize="xs"
                        >
                          Phlebotomist
                        </Button>
                        {/* Phase 2 lab timeline: Mark Shipped / Undo */}
                        {!batch.shipped_at ? (
                          <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            leftIcon={<FaShippingFast />}
                            onClick={() => handleMarkShippedClick(batch)}
                            fontSize="xs"
                          >
                            Mark Shipped
                          </Button>
                        ) : (
                          <Tooltip label={`Shipped ${batch.carrier ? '· ' + batch.carrier : ''}${batch.tracking_number ? ' · ' + batch.tracking_number : ''} — click to undo`}>
                            <Button
                              size="sm"
                              colorScheme="green"
                              variant="outline"
                              leftIcon={<FaCheckCircle />}
                              onClick={() => undoShipped(batch)}
                              fontSize="xs"
                            >
                              Shipped
                            </Button>
                          </Tooltip>
                        )}
                        {invoicePaymentStatuses[batch.id] !== 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setBatchToMarkPaidManually(batch);
                              setManualPaymentConfirmOpen(true);
                            }}
                            fontSize="xs"
                          >
                            Manual Payment
                          </Button>
                        )}
                        {invoicePaymentStatuses[batch.id] !== 'paid' ? (
                          <Button
                            size="sm"
                            colorScheme="green"
                            leftIcon={<FaCreditCard />}
                            onClick={() => handleChargeClick(batch)}
                            isLoading={chargingInvoice}
                            isDisabled={chargingInvoice}
                            fontSize="xs"
                            fontWeight="semibold"
                          >
                            Charge
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            leftIcon={<FaUndo />}
                            onClick={() => handleUnmarkPaidClick(batch)}
                            isLoading={unmarkingPaidId === batch.id}
                            isDisabled={!!unmarkingPaidId}
                            fontSize="xs"
                          >
                            Unmark Paid
                          </Button>
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Box p={4} borderTop="1px solid" borderColor="gray.100">
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Text fontSize="sm" color="gray.600">
              {filteredBatches.length === 0
                ? 'No invoices to display'
                : `Showing ${batchTableStartIndex + 1} to ${Math.min(batchTableStartIndex + batchTablePerPage, filteredBatches.length)} of ${filteredBatches.length} invoices`}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => setBatchTablePage((p) => Math.max(1, p - 1))}
                isDisabled={batchTablePage === 1 || batchTableTotalPages <= 1}
              >
                Previous
              </Button>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                Page {batchTablePage} of {batchTableTotalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setBatchTablePage((p) => Math.min(batchTableTotalPages, p + 1))}
                isDisabled={batchTablePage === batchTableTotalPages || batchTableTotalPages <= 1}
              >
                Next
              </Button>
              <Select
                size="sm"
                value={batchTablePerPage}
                onChange={(e) => {
                  setBatchTablePerPage(parseInt(e.target.value, 10));
                  setBatchTablePage(1);
                }}
                w="110px"
              >
                <option value={10}>10/page</option>
                <option value={25}>25/page</option>
                <option value={50}>50/page</option>
              </Select>
            </HStack>
          </HStack>
        </Box>
      </Card>

      {/* Create Invoice Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Invoice</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Invoice Name (Optional)</FormLabel>
                <Input
                  value={newBatchName}
                  onChange={(e) => setNewBatchName(e.target.value)}
                  placeholder="Optional: e.g., January 2025 Shipment (Invoice number will be auto-generated)"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newBatchDescription}
                  onChange={(e) => setNewBatchDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleCreateBatch}>
              Create Invoice
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Invoice Details Modal */}
      <Modal isOpen={isBatchDetailOpen} onClose={onBatchDetailClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Batch Details: {selectedBatch?.batch_number || 'N/A'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBatch && selectedBatch.id ? (
              <VStack spacing={4} align="stretch">
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="bold">Batch Number:</Text>
                    {!editingBatchNumber && (
                      <IconButton
                        size="sm"
                        icon={<EditIcon />}
                        onClick={handleEditBatchNumber}
                        aria-label="Edit batch number"
                      />
                    )}
                  </HStack>
                  {editingBatchNumber ? (
                    <VStack align="stretch" spacing={2}>
                      <Input
                        value={editedBatchNumber}
                        onChange={(e) => setEditedBatchNumber(e.target.value)}
                        placeholder="Enter batch number (e.g., BATCH-2026-004)"
                      />
                      <HStack>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={handleSaveBatchNumber}
                          isLoading={savingBatchNumber}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEditBatchNumber}
                          isDisabled={savingBatchNumber}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <Text>{selectedBatch.batch_number || 'N/A'}</Text>
                  )}
                </Box>
                {selectedInvoice && (
                  <Box>
                    <Text fontWeight="bold">Invoice Number:</Text>
                    <Text>{selectedInvoice.invoice_number || 'N/A'}</Text>
                  </Box>
                )}
                <Box>
                  <Text fontWeight="bold">Batch Name:</Text>
                  <Text>{selectedBatch.batch_name || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Status:</Text>
                  <Badge colorScheme={selectedBatch.status === 'shipped' ? 'green' : 'yellow'}>
                    {selectedBatch.status || 'pending'}
                  </Badge>
                </Box>
                {selectedInvoice && (
                  <Box>
                    <Text fontWeight="bold">Payment Status:</Text>
                    <Badge colorScheme={selectedInvoice.payment_status === 'paid' ? 'green' : selectedInvoice.payment_status === 'pending' ? 'yellow' : selectedInvoice.payment_status === 'failed' ? 'red' : 'gray'}>
                      {selectedInvoice.payment_status || 'unpaid'}
                    </Badge>
                    {selectedInvoice.payment_method_last_4 && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Card: •••• {selectedInvoice.payment_method_last_4}
                      </Text>
                    )}
                    {selectedInvoice.stripe_invoice_id && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Stripe Invoice: {selectedInvoice.stripe_invoice_id}
                      </Text>
                    )}
                  </Box>
                )}
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>Submissions in Invoice:</Text>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Patient</Th>
                        <Th>Draw Fee</Th>
                        <Th>Admin Fee</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {submissions
                        .filter(s => s.batch_id === selectedBatch.id)
                        .map((sub) => {
                          const phlebotomist = sub.phlebotomist_id ? phlebotomists[sub.phlebotomist_id] : null;
                          const drawFee = parseFloat(phlebotomist?.lab_draw_fee || 0);
                          const adminFee = effectiveAdminFee(sub);
                          return (
                            <Tr key={sub.id}>
                              <Td>{sub.patient_name || 'N/A'}</Td>
                              <Td color={sub.draw_fee_paid ? 'green.500' : undefined}>{sub.draw_fee_paid ? '$0.00 (Paid)' : `$${drawFee.toFixed(2)}`}</Td>
                              <Td>${adminFee.toFixed(2)}</Td>
                              <Td>
                                <IconButton
                                  size="xs"
                                  icon={<DeleteIcon />}
                                  onClick={() => handleRemoveSubmissionFromBatch(sub.id)}
                                  aria-label="Remove from invoice"
                                />
                              </Td>
                            </Tr>
                          );
                        })}
                    </Tbody>
                  </Table>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>Shipping Method</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel mb={2}>Method</FormLabel>
                      <Select
                        value={shippingForm.shippingMethod}
                        onChange={(e) => handleShippingFieldChange('shippingMethod', e.target.value)}
                      >
                        <option value="fedex">FedEx Shipment</option>
                        <option value="drop_off">Drop Off</option>
                        <option value="delivery">Delivery / Courier</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel mb={2}>
                        Ship Fee ($)
                        {shippingForm.shippingMethod === 'drop_off' && (
                          <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal" ml={2}>
                            (Fixed $10.00)
                          </Text>
                        )}
                        {shippingForm.shippingMethod === 'fedex' && (
                          <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal" ml={2}>
                            (FedEx Cost + $10.00)
                          </Text>
                        )}
                      </FormLabel>
                      <NumberInput
                        value={shippingForm.shipFee}
                        min={0}
                        precision={2}
                        onChange={(valueString) => handleShippingFieldChange('shipFee', valueString)}
                        isReadOnly={shippingForm.shippingMethod === 'drop_off'}
                      >
                        <NumberInputField placeholder={shippingForm.shippingMethod === 'drop_off' ? '10.00' : '0.00'} />
                        {shippingForm.shippingMethod !== 'drop_off' && (
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        )}
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  {shippingForm.shippingMethod === 'fedex' && (
                    <VStack align="stretch" spacing={3} mt={4}>
                      <FormControl>
                        <FormLabel>Tracking Number</FormLabel>
                        <Input
                          value={shippingForm.trackingNumber}
                          onChange={(e) => handleShippingFieldChange('trackingNumber', e.target.value)}
                          placeholder="Enter tracking number or generate FedEx label"
                        />
                      </FormControl>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} alignItems="flex-start">
                        <FormControl>
                          <FormLabel mb={2}>FedEx Label URL</FormLabel>
                          <Input
                            value={shippingForm.labelUrl}
                            onChange={(e) => handleShippingFieldChange('labelUrl', e.target.value)}
                            placeholder="https://"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel mb={2}>Service Type</FormLabel>
                          <Input
                            value={shippingForm.serviceType}
                            onChange={(e) => handleShippingFieldChange('serviceType', e.target.value)}
                            placeholder="FEDEX_2_DAY"
                          />
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} alignItems="flex-start">
                        <FormControl>
                          <FormLabel mb={2}>FedEx Shipping Cost ($)</FormLabel>
                          <FormHelperText mt={0} mb={2}>
                            (Will add $10.00 service fee automatically)
                          </FormHelperText>
                          <NumberInput
                            value={shippingForm.shippingCost}
                            min={0}
                            precision={2}
                            onChange={(valueString) => {
                              handleShippingFieldChange('shippingCost', valueString);
                              // Auto-update ship fee when FedEx cost changes
                              if (valueString !== '' && shippingForm.shippingMethod === 'fedex') {
                                const fedexCost = parseFloat(valueString);
                                if (!isNaN(fedexCost)) {
                                  handleShippingFieldChange('shipFee', (fedexCost + 10).toFixed(2));
                                }
                              }
                            }}
                          >
                            <NumberInputField placeholder="0.00" />
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel mb={2}>Submission for FedEx Label</FormLabel>
                          <Select
                            value={selectedFedexSubmissionId || ''}
                            onChange={(e) => setSelectedFedexSubmissionId(e.target.value)}
                          >
                            <option value="">Select submission</option>
                            {submissions
                              .filter(sub => sub.batch_id === selectedBatch.id)
                              .map(sub => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.patient_name || 'Unnamed'} ({String(sub.id).slice(0, 8)})
                                </option>
                              ))}
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                      <HStack spacing={3}>
                        <Button colorScheme="purple" onClick={openFedexModal}>
                          Generate FedEx Label
                        </Button>
                        {shippingForm.labelUrl && (
                          <Button as="a" href={shippingForm.labelUrl} target="_blank" rel="noopener noreferrer">
                            View Label
                          </Button>
                        )}
                      </HStack>
                    </VStack>
                  )}

                  {shippingForm.shippingMethod === 'drop_off' && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={4}>
                      <FormControl>
                        <FormLabel>Drop-Off Location</FormLabel>
                        <Input
                          value={shippingForm.dropoffLocation}
                          onChange={(e) => handleShippingFieldChange('dropoffLocation', e.target.value)}
                          placeholder="Lab address or facility"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Contact Person / Phone</FormLabel>
                        <Input
                          value={shippingForm.dropoffContact}
                          onChange={(e) => handleShippingFieldChange('dropoffContact', e.target.value)}
                          placeholder="Name & phone"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Drop-Off Window</FormLabel>
                        <Input
                          type="datetime-local"
                          value={shippingForm.dropoffWindow}
                          onChange={(e) => handleShippingFieldChange('dropoffWindow', e.target.value)}
                        />
                      </FormControl>
                    </SimpleGrid>
                  )}

                  {shippingForm.shippingMethod === 'delivery' && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={4}>
                      <FormControl>
                        <FormLabel>Courier / Driver Name</FormLabel>
                        <Input
                          value={shippingForm.deliveryPerson}
                          onChange={(e) => handleShippingFieldChange('deliveryPerson', e.target.value)}
                          placeholder="Person responsible for delivery"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Courier Contact</FormLabel>
                        <Input
                          value={shippingForm.deliveryContact}
                          onChange={(e) => handleShippingFieldChange('deliveryContact', e.target.value)}
                          placeholder="Phone or email"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Delivery Window</FormLabel>
                        <Input
                          type="datetime-local"
                          value={shippingForm.deliveryWindow}
                          onChange={(e) => handleShippingFieldChange('deliveryWindow', e.target.value)}
                        />
                      </FormControl>
                    </SimpleGrid>
                  )}

                  <FormControl mt={4}>
                    <FormLabel>Shipping Notes</FormLabel>
                    <Textarea
                      value={shippingForm.shippingNotes}
                      onChange={(e) => handleShippingFieldChange('shippingNotes', e.target.value)}
                      placeholder="Any additional instructions or confirmations"
                    />
                  </FormControl>

                  <HStack justify="flex-end" mt={4}>
                    <Button colorScheme="blue" onClick={handleSaveShipping} isLoading={savingShipping}>
                      Save Shipping Details
                    </Button>
                  </HStack>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>Invoice Fees:</Text>
                  {(() => {
                    const fees = calculateBatchFees(selectedBatch);
                    return (
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Total Draw Fees:</Text>
                          <Text fontWeight="bold">${fees.totalDrawFee.toFixed(2)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Total Admin Fees:</Text>
                          <Text fontWeight="bold">${fees.totalAdminFee.toFixed(2)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Ship Fee:</Text>
                          <Text fontWeight="bold">${fees.shipFee.toFixed(2)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Total:</Text>
                          <Text fontWeight="bold" color="green.600" fontSize="lg">
                            ${fees.total.toFixed(2)}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    );
                  })()}
                </Box>
              </VStack>
            ) : (
              <Text>Loading invoice details...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Flex wrap="wrap" gap={2} justify="flex-end" width="100%">
              <Button variant="ghost" onClick={onBatchDetailClose}>
                Close
              </Button>
              {selectedInvoice?.payment_status === 'paid' ? (
                <Badge colorScheme="green" p={2} alignSelf="center">
                  Already Paid
                </Badge>
              ) : (
                <Button
                  colorScheme="green"
                  onClick={handleChargeInvoice}
                  isLoading={chargingInvoice}
                  isDisabled={chargingInvoice || !selectedBatch}
                >
                  {selectedInvoice ? 'Charge Invoice' : 'Generate & Charge Invoice'}
                </Button>
              )}
              <Button
                colorScheme="blue"
                leftIcon={<DownloadIcon />}
                onClick={() => generateBatchInvoice(selectedBatch)}
              >
                Create Invoice
              </Button>
              <Button
                variant="outline"
                colorScheme="blue"
                leftIcon={<DownloadIcon />}
                onClick={() => generateMobileLabInvoice(selectedBatch)}
                isLoading={generatingMobileLabBatchId === selectedBatch?.id}
                isDisabled={!selectedBatch || !!generatingMobileLabBatchId}
              >
                Phlebotomist
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FedExLabelGenerator
        submissionId={selectedFedexSubmissionId}
        isOpen={fedexModalOpen}
        onClose={closeFedexModal}
        onLabelGenerated={handleBatchFedexLabelGenerated}
      />

      {/* Charge Confirmation Dialog */}
      <AlertDialog
        isOpen={chargeConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setChargeConfirmOpen(false);
          setBatchToCharge(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Charge Invoice
            </AlertDialogHeader>
            <AlertDialogBody>
              {batchToCharge && (() => {
                const fees = calculateBatchFees(batchToCharge);
                const lab = getBatchLab(batchToCharge);
                const hasStripe = batchLabHasStripe(batchToCharge);
                
                return (
                  <>
                    <Text mb={2}>
                      Are you sure you want to charge this invoice?
                    </Text>
                    <Text fontWeight="bold" mb={2}>
                      Batch: {batchToCharge.batch_name || batchToCharge.batch_number || 'N/A'}
                    </Text>
                    {lab && (
                      <>
                        <Text mb={1}>
                          <strong>Lab:</strong> {lab.name}
                        </Text>
                        {hasStripe ? (
                          <Text mb={1} fontSize="sm" color="green.600">
                            ✓ Stripe Customer ID: {lab.stripe_customer_id?.substring(0, 20)}...
                          </Text>
                        ) : (
                          <Alert status="error" mt={3} mb={3} borderRadius="md">
                            <AlertIcon />
                            <Box flex="1">
                              <AlertTitle>Payment method not set up</AlertTitle>
                              <AlertDescription>
                                <strong>{lab.name}</strong> has not set up Stripe payment. You cannot charge this invoice until the lab has a payment method configured in Lab Management. Please go to Lab Management, open this lab, and set up their Stripe customer / payment method first.
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}
                      </>
                    )}
                    <Text mb={2} mt={2}>
                      <strong>Total Amount: ${fees.total.toFixed(2)}</strong>
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {hasStripe
                        ? "This will charge the lab's payment method on file via Stripe. The invoice will be generated automatically if it doesn't exist yet."
                        : "Charge is disabled until the lab sets up payment in Lab Management."
                      }
                    </Text>
                  </>
                );
              })()}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setChargeConfirmOpen(false);
                  setBatchToCharge(null);
                }}
              >
                Cancel
              </Button>
              {batchToCharge && (
                <Button
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => handleCreatePaymentLink(batchToCharge)}
                  ml={3}
                  isDisabled={chargingInvoice || creatingPaymentLinkBatchId !== null}
                  isLoading={creatingPaymentLinkBatchId === batchToCharge.id}
                >
                  Payment Link
                </Button>
              )}
              <Button
                colorScheme="green"
                onClick={confirmChargeInvoice}
                ml={3}
                isLoading={chargingInvoice}
                isDisabled={batchToCharge && !batchLabHasStripe(batchToCharge)}
              >
                Charge Invoice
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Unmark Paid Confirmation Dialog */}
      <AlertDialog
        isOpen={unmarkPaidConfirmOpen}
        leastDestructiveRef={cancelUnmarkRef}
        onClose={() => {
          setUnmarkPaidConfirmOpen(false);
          setBatchToUnmarkPaid(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Unmark Invoice as Paid
            </AlertDialogHeader>
            <AlertDialogBody>
              {batchToUnmarkPaid && (() => {
                return (
                  <>
                    <Text mb={2}>
                      Are you sure you want to mark this invoice as unpaid?
                    </Text>
                    <Text fontWeight="bold" mb={2}>
                      Batch: {batchToUnmarkPaid.batch_name || batchToUnmarkPaid.batch_number || 'N/A'}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={2}>
                      This will revert the payment status to "unpaid". Use this if the invoice was marked paid by mistake or if payment needs to be re-processed.
                    </Text>
                  </>
                );
              })()}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelUnmarkRef}
                onClick={() => {
                  setUnmarkPaidConfirmOpen(false);
                  setBatchToUnmarkPaid(null);
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={confirmUnmarkPaid}
                ml={3}
                isLoading={unmarkingPaidId !== null}
              >
                Unmark as Paid
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manual Payment Confirmation Dialog */}
      <AlertDialog
        isOpen={manualPaymentConfirmOpen}
        leastDestructiveRef={cancelManualPaymentRef}
        onClose={() => {
          setManualPaymentConfirmOpen(false);
          setBatchToMarkPaidManually(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Mark Invoice as Paid
            </AlertDialogHeader>
            <AlertDialogBody>
              {batchToMarkPaidManually && (() => {
                const fees = calculateBatchFees(batchToMarkPaidManually);
                const lab = getBatchLab(batchToMarkPaidManually);
                return (
                  <>
                    <Text mb={2}>
                      Are you sure you want to mark this invoice as paid?
                    </Text>
                    <Text fontWeight="bold" mb={2}>
                      Batch: {batchToMarkPaidManually.batch_name || batchToMarkPaidManually.batch_number || 'N/A'}
                    </Text>
                    {lab && (
                      <Text fontWeight="bold" mb={2}>
                        Lab: {lab.name}
                      </Text>
                    )}
                    <Text fontWeight="bold" mb={4} fontSize="lg" color="green.600">
                      Total Amount: ${fees.total.toFixed(2)}
                    </Text>
                    <Alert status="warning" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription>
                        <strong>No payment will be processed.</strong> This will only mark the invoice as paid in the system. Make sure payment has been received through another method (check, wire transfer, etc.) before confirming.
                      </AlertDescription>
                    </Alert>
                  </>
                );
              })()}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelManualPaymentRef}
                onClick={() => {
                  setManualPaymentConfirmOpen(false);
                  setBatchToMarkPaidManually(null);
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={confirmManualPayment}
                ml={3}
                isLoading={markingManualPayment}
              >
                Mark as Paid
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Phase 2 lab timeline: Mark Shipped Modal */}
      <Modal isOpen={shipModalOpen} onClose={() => { setShipModalOpen(false); setBatchToShip(null); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaShippingFast} color="orange.500" />
              <Text>Mark Batch Shipped</Text>
            </HStack>
            {batchToShip && (
              <Text fontSize="sm" color="gray.500" fontWeight="500" mt={1}>
                {batchToShip.batch_number || batchToShip.batch_name}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Confirming will set the batch as shipped and the lab will see it as <strong>In Transit</strong> in their portal.
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Carrier</FormLabel>
                <Input
                  size="sm"
                  placeholder="FedEx, UPS, USPS, local courier..."
                  value={shipForm.carrier}
                  onChange={(e) => setShipForm({ ...shipForm, carrier: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Tracking Number <Text as="span" color="gray.400" fontSize="xs">(optional)</Text></FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. 1Z999AA10123456784"
                  value={shipForm.tracking_number}
                  onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Estimated Arrival <Text as="span" color="gray.400" fontSize="xs">(optional)</Text></FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={shipForm.estimated_arrival}
                  onChange={(e) => setShipForm({ ...shipForm, estimated_arrival: e.target.value })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => { setShipModalOpen(false); setBatchToShip(null); }}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              leftIcon={<FaShippingFast />}
              onClick={confirmMarkShipped}
              isLoading={savingShip}
            >
              Mark Shipped
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default BatchManagement;


